import { Change } from "../fsm/change";
import { PhonemeInfo } from "../pattern/phonemeInfo";
import { IpaType } from "../phonemes/ipa";
import { SyllablePhonotactics } from "../phonotactics/phonotactics";
import { StressRule } from "../stress/stressRule";
import { CompiledRule } from "./compileRule";
import { DiacriticRule } from "./diacriticRule";
import { SegmentStrategy } from "./intoSegments";
import { LengthRules, PhoneLengths } from "./lengthRules";
import { SetRules } from "./replaceLexicalSets";
import { Rule } from "./rule";

/**
 * a set of rules describing ways that phonemes change
 */
export interface RuleSet {
  /** options for modifying the input before any rules are run */
  readonly preprocess?: PreprocessOptions[];
  /** replace a full raw word (e.g. English) with phonemes */
  readonly wholeWords?: Record<string, IpaType>;
  /** use English RP dictionary rather than default General American */
  readonly useRPDictionary?: boolean;
  /** if the input is English, these describe how common sets of vowels change */
  readonly lexicalSets?: SetRules;
  /** use to add syllable breaks */
  readonly syllableBreaks?: SyllablePhonotactics<IpaType> | IpaType[];
  /** how to break words into segments of phonemes before running rules, default 'Ipa' */
  readonly inputSegments?: SegmentStrategy;
  readonly outputSegments?: SegmentStrategy;
  /** how to break words into segments of phonemes before running rules2, default input/outputSegments */
  readonly inputSegments2?: SegmentStrategy;
  readonly outputSegments2?: SegmentStrategy;
  /** by default rules are run in specified order, but you can sort by length of the input if useful */
  readonly sortRulesByInputLength?: boolean;
  /** specified 'from' segments only need to match a subset, e.g., the pattern /a/ matches /aː/ */
  readonly subsetMatches?: boolean;
  /** general phoneme replacement rules */
  readonly rules?: RuleType[];
  /** rerun rules w/out diacritics & replace with a different diacritic */
  readonly diacriticRules?: DiacriticRule[];
  /** occasionally it's useful to have additional independent phases */
  readonly rules2?: Rules2Type;
  /** a list of phoneme & their desired length in milliseconds, used only when supported */
  readonly phonemeLengths?: LengthRules;
  /** use foma rules for a finite state machine */
  readonly fsm?: string;
  /** how to place stress markers */
  readonly stressRule?: StressRule<IpaType>;
  /** a description of syllable phonotactics */
  readonly phonotactics?: SyllablePhonotactics<IpaType>;
};

/** rule list consists of rules & comments */
export type RuleType = Rule | RuleComment;
/** rules2: can be a list of rules or multiple lists */
export type Rules2Type = RuleType[] | RuleType[][];

export interface RuleComment {
  readonly comment: string;
  readonly isBlankLine?: boolean;
};
export function isRule(r: Rule | RuleComment): r is Rule {
  return r !== undefined && (r as RuleComment).comment === undefined;
}
export function isComment(r: Rule | RuleComment): r is RuleComment {
  return r !== undefined && (r as RuleComment).comment !== undefined;
}
export function getJustRules(rules: RuleType[] | undefined): Rule[] | undefined {
  return rules?.filter(r => (r as RuleComment).comment === undefined) as Rule[];
}

export interface CompiledRuleSet {
  readonly preprocess?: PreprocessOptions[];
  readonly wholeWords?: Record<string, IpaType>;
  readonly lexicalSetLookup: string[]; // = rulesToLookup(lexicalSets);
  readonly syllableBreaks?: SyllablePhonotactics<PhonemeInfo> | IpaType[];
  readonly inputSegments: SegmentStrategy;
  readonly outputSegments: SegmentStrategy;
  readonly useRPDictionary?: boolean;
  readonly subsetMatches?: boolean;
  readonly rules: CompiledRule[];
  readonly diacriticRules?: DiacriticRule[];
  readonly rules2?: CompiledRule[][];
  readonly addPhonemeLength?: PhoneLengths;
  readonly fsm?: Change[];
  readonly stressRule?: StressRule<PhonemeInfo>;
  readonly phonotactics: SyllablePhonotactics<PhonemeInfo> | IpaType[];
};

export type PreprocessOptions =
  /** make everything lower case */
  | 'lowerCase'
  /** capture punctuation as pauses */
  | 'cleanPunctuation'
  /** normalize IPA so there's a single way to indicate things */
  | 'normIpa'
  /** convert ligatures like ﬁ into standard characters */
  | 'cleanLigatures'
  ;
